import { BookerPosition } from "../App";
import { SingleProductModal } from "./SingleProductModal";
import { BookerContextProvider, CartContextProvider } from "../contexts";
import { I18NContextProvider } from "widgets/shared";
import { i18nLanguage, i18nNamespace } from "i18n";
import { useMemo } from "react";
import { BookerByIdQuery } from "gql/generated";
import type {
  DefaultContact,
  PaymentRequestAppliedDiscount,
  PaymentRequestLineItem,
  UserOperator,
} from "../contexts/BookerContextProvider/BookerContext";
import { CartSnapshot } from "../contexts/CartContextProvider";
import type { MembershipCheckoutIntent } from "shared/utils/membershipIntent";

type BookerModalProps = {
  defaultBooker?: BookerByIdQuery["bookerById"];
  bookerId: string;
  open: boolean;
  onClose: () => void;
  bookerPosition: BookerPosition;
  isEmbedded?: boolean;
  language: i18nLanguage;
  hasOpenedOnce?: boolean;
  broadcastId?: string;
  broadcastResourceId?: string;
  defaultContact?: DefaultContact;
  userOperators?: UserOperator[];
  paymentRequestId?: string;
  paymentRequestVersion?: number;
  paymentRequestGrandTotal?: number;
  paymentRequestSubtotal?: number;
  paymentRequestTaxAmount?: number;
  paymentRequestTaxLines?: {
    name: string;
    ratePercent?: number | null;
    amount: number;
  }[];
  paymentRequestCustomerFee?: number | null;
  paymentRequestAdditionalLineItems?: PaymentRequestLineItem[];
  paymentRequestAppliedDiscount?: PaymentRequestAppliedDiscount;
  initialCartSnapshot?: CartSnapshot;
  readOnlySelection?: boolean;
  disableResetOnOpen?: boolean;
  invoiceAdminMode?: boolean;
  isStorefrontOwner?: boolean;
  storefrontId?: string;
  organizationId?: string;
  membershipIntent?: MembershipCheckoutIntent;
};

export function BookerModal({
  bookerId,
  open,
  onClose,
  bookerPosition,
  isEmbedded = false,
  language,
  hasOpenedOnce = false,
  defaultBooker,
  broadcastId,
  broadcastResourceId,
  defaultContact,
  userOperators,
  paymentRequestId,
  paymentRequestVersion,
  paymentRequestGrandTotal,
  paymentRequestSubtotal,
  paymentRequestTaxAmount,
  paymentRequestTaxLines,
  paymentRequestCustomerFee,
  paymentRequestAdditionalLineItems,
  paymentRequestAppliedDiscount,
  initialCartSnapshot,
  readOnlySelection,
  disableResetOnOpen,
  invoiceAdminMode,
  isStorefrontOwner,
  storefrontId,
  organizationId,
  membershipIntent,
}: BookerModalProps) {
  // Memoize the context values to prevent unnecessary re-renders
  const bookerContextValue = useMemo(
    () => ({
      onCloseBookerModal: onClose,
      bookerId,
      isEmbedded,
      language,
      hasOpenedOnce,
      defaultBooker,
      bookerModalOpen: open,
      bookerPosition,
      broadcastId,
      broadcastResourceId,
      defaultContact,
      userOperators,
      paymentRequestId,
      paymentRequestVersion,
      paymentRequestGrandTotal,
      paymentRequestSubtotal,
      paymentRequestTaxAmount,
      paymentRequestTaxLines,
      paymentRequestCustomerFee,
      paymentRequestAdditionalLineItems,
      paymentRequestAppliedDiscount,
      initialCartSnapshot,
      readOnlySelection,
      disableResetOnOpen,
      invoiceAdminMode,
      isStorefrontOwner,
      storefrontId,
      organizationId,
      membershipIntent,
    }),
    [
      onClose,
      bookerId,
      isEmbedded,
      language,
      hasOpenedOnce,
      defaultBooker,
      open,
      bookerPosition,
      broadcastId,
      broadcastResourceId,
      defaultContact,
      userOperators,
      paymentRequestId,
      paymentRequestVersion,
      paymentRequestGrandTotal,
      paymentRequestSubtotal,
      paymentRequestTaxAmount,
      paymentRequestTaxLines,
      paymentRequestCustomerFee,
      paymentRequestAdditionalLineItems,
      paymentRequestAppliedDiscount,
      initialCartSnapshot,
      readOnlySelection,
      disableResetOnOpen,
      invoiceAdminMode,
      isStorefrontOwner,
      storefrontId,
      organizationId,
      membershipIntent,
    ],
  );

  const i18nContextValue = useMemo(
    () => ({
      defaultNS: [i18nNamespace.Core, i18nNamespace.Booker],
      // Embedded checkout runs from `bookerModal/main.js`; locales live under `/widget/bookerModal/`.
      i18nWidgetType: isEmbedded ? ("bookerModal" as const) : ("booker" as const),
      i18nLang: language,
    }),
    [language, isEmbedded],
  );

  // Don't render if bookerId is empty
  if (!bookerId) return null;

  return (
    <I18NContextProvider {...i18nContextValue}>
      <BookerContextProvider {...bookerContextValue}>
        <CartContextProvider>
          <SingleProductModal open={open} />
        </CartContextProvider>
      </BookerContextProvider>
    </I18NContextProvider>
  );
}
